import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { animate, state, style, transition, trigger } from '@angular/animations';
import moment from 'moment';

import { MaterialModule } from 'src/app/app.materials';
import { LineChartComponent } from '../line-chart/line-chart.component';
import { CommonModule } from '@angular/common';
import { PortfolioService } from '../../pages/portfolio/portfolio.service';

interface InfoField {
  id: string;
  title: string;
  unit: string;
  type?: 'money';
}

@Component({
  selector: 'app-etf-info-dialog',
  templateUrl: './etf-info-dialog.component.html',
  styleUrls: ['../company-info-dialog/company-info-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('100ms ease')),
    ]),
  ],
  standalone: true,
  imports: [MaterialModule, LineChartComponent, CommonModule],
})
export class EtfInfoDialogComponent implements OnInit {
  readonly sectionsA: InfoField[] = [
    { id: 'name', title: 'Name', unit: '' },
    { id: 'broad_geo_desc', title: 'Region', unit: '' },
    { id: 'specific_geo_desc', title: 'Country', unit: '' },
    { id: 'sector', title: 'Sector', unit: '' },
    { id: 'focus_desc', title: 'Strategy', unit: '' },
    { id: 'econ_development_desc', title: 'Economic Development', unit: '' },
    { id: 'niche_desc', title: 'Niche', unit: '' },
    { id: 'aum', title: 'AUM', unit: 'M$', type: 'money' },
    { id: 'asset_class', title: 'Asset Class', unit: '' },
  ];

  readonly sectionsB: InfoField[] = [
    { id: 'brand_name', title: 'Brand Name', unit: '' },
    { id: 'category_desc', title: 'Category Description', unit: '' },
    { id: 'segment_desc', title: 'Segment Description', unit: '' },
    { id: 'isin', title: 'Isin', unit: '' },
    { id: 'iso_country', title: 'Iso country', unit: '' },
    { id: 'currency', title: 'Currency', unit: '' },
    { id: 'active_desc', title: 'Active desc', unit: '' },
    { id: 'nav_date', title: 'Price date', unit: '' },
    { id: 'launch_date', title: 'Launch date', unit: '' },
  ];

  readonly companyName = signal('');
  readonly overview = signal<Record<string, any>>({});
  readonly closeAdjData = signal<any[]>([]);

  // Built from the company currency once on init; using a computed signal keeps
  // the chart tooltip aligned with the displayed currency without manual CD.
  private readonly currency = signal('USD');
  readonly extraOptions = computed(() => ({
    tooltip: {
      // Override stockChart's `outside: true` / `split: true` defaults so the
      // tooltip uses the shared design-token box.
      outside: false,
      split: false,
      xDateFormat: '%Y-%m-%d',
      valueSuffix: ` ${this.currency()}`,
    },
  }));

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public dialogRef: MatDialogRef<EtfInfoDialogComponent>,
    private service: PortfolioService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.initDialog();
  }

  private initDialog(): void {
    const company = this.data?.company;
    if (!company) {
      return;
    }

    this.currency.set(this.resolveCurrency(company));
    this.companyName.set(company.company_name ?? '');
    this.overview.set(company.etf_reference_table ?? {});

    this.fetchAdjChart(company.id);
  }

  private resolveCurrency(company: any): string {
    const raw = company?.currency;
    if (!raw) {
      return 'USD';
    }
    return typeof raw === 'string' ? raw : raw.code ?? 'USD';
  }

  private fetchAdjChart(companyId: number | string): void {
    this.service
      .getETFCloseAdj(companyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: any) => this.applyAdjChartResponse(resp),
      });
  }

  private applyAdjChartResponse(resp: any): void {
    if (resp?.etf_reference) {
      this.overview.set(resp.etf_reference);
    }

    const items: any[] = resp?.items ?? [];
    if (!items.length) {
      return;
    }

    const closeAdj: [number, number][] = items
      .map((r): [number, number] => [
        moment(`${r.date} 12:00:00`).unix() * 1000,
        r.close_adj,
      ])
      .sort((a, b) => a[0] - b[0]);

    const lastDate = moment(closeAdj[closeAdj.length - 1][0]).format('YYYY-MM-DD');
    this.overview.update((prev) => ({ ...prev, nav_date: lastDate }));

    this.closeAdjData.set([
      {
        name: this.companyName(),
        data: closeAdj,
        tooltip: { valueDecimals: 2 },
      },
    ]);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
